import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationKey } from '../../i18n/translationKeys';
import { IgnoreRelanceModal, type IgnoreRelanceSubmission } from './IgnoreRelanceModal';
import { useCollectPayment } from '../../hooks/useCollectPayment';
import { LinkButton } from '../../ui/LinkButton/LinkButton';

interface FollowUpRecapActionsProps {
    relanceId: string;
    customerName: string;
    customerExternalId: number | null;
    totalDue: number | null;
    formattedTotalDue: string | null;
    showPaidAction: boolean;
    /** Resolves true when the change was saved; the modal stays open, with its input, on false. */
    onDeactivate: (submission: IgnoreRelanceSubmission) => Promise<boolean>;
}

export const FollowUpRecapActions: React.FC<FollowUpRecapActionsProps> = ({
    relanceId,
    customerName,
    customerExternalId,
    totalDue,
    formattedTotalDue,
    showPaidAction,
    onDeactivate,
}) => {
    const { t } = useTranslation();
    const [modalOpen, setModalOpen] = useState(false);
    const [paidActionError, setPaidActionError] = useState(false);
    const collectPayment = useCollectPayment();

    const handlePaidClick = () =>
        setPaidActionError(!collectPayment({ id: relanceId, customerExternalId, totalDue: totalDue ?? 0 }));

    return (
        <>
            <div data-testid="followup-recap-actions" className="flex flex-col items-start gap-s">
                {showPaidAction && (
                    <LinkButton variant="navigation" weight="medium" chevron="end" data-testid="followup-paid-link" onClick={handlePaidClick}>
                        {t(TranslationKey.CustomerFollowUp.Recap.CollectBalance)}
                    </LinkButton>
                )}
                {paidActionError && (
                    <p data-testid="followup-paid-error" className="text-xs text-danger">
                        {t(TranslationKey.Home.PaidAction.UnreachableError)}
                    </p>
                )}
                <LinkButton variant="navigation" weight="medium" chevron="end" data-testid="followup-deactivate-link" onClick={() => setModalOpen(true)}>
                    {t(TranslationKey.CustomerFollowUp.Recap.SnoozeOrIgnore)}
                </LinkButton>
            </div>
            {modalOpen && (
                <IgnoreRelanceModal
                    taskId={relanceId}
                    customerName={customerName}
                    formattedAmount={formattedTotalDue}
                    onCancel={() => setModalOpen(false)}
                    onConfirm={async submission => {
                        if (await onDeactivate(submission)) setModalOpen(false);
                    }}
                />
            )}
        </>
    );
};
